import React, { useMemo } from 'react';
import { Box, styled } from '@mui/material';
import { invert } from 'lodash';
import CalendarDropdownMultiSelect from '@/components/Focus/common/CalendarDropdownMultiSelect';
import { Bullet } from '@/components/_common/Table/Calendar/CalendarComponents/common';
import { getColorByElementStatus } from '@/components/_common/Table/Calendar/utils';
import { ElementInventoryStatus } from '@/redux/slices/slice_types';
import {
  elementStatusToFilterStatusMap,
  StatusFilter as StatusFilterEnum
} from '@/components/_common/Table/Calendar/SidePanel/components/ElementReadiness.constants';

interface Props {
  values: StatusFilterEnum[];
  onChange: (status: StatusFilterEnum[]) => void;
}

const filterStatusToElementStatusMap = invert(elementStatusToFilterStatusMap) as Record<StatusFilterEnum, ElementInventoryStatus>;

const StatusFilter: React.FC<Props> = ({ values, onChange }) => {
  const statusFilterOptions = useMemo(
    () => [
      { label: 'Ready', value: StatusFilterEnum.Ready },
      { label: 'Pending', value: StatusFilterEnum.Pending },
      { label: 'Attention', value: StatusFilterEnum.Attention }
    ],
    []
  );

  const handleStatusChange = (status: StatusFilterEnum, newValue: boolean) => {
    onChange(newValue ? [...values, status] : values.filter((value) => value !== status));
  };

  const handleToggleAllStatuses = () => {
    onChange(values.length > 0 ? [] : statusFilterOptions.map((o) => o.value));
  };

  return (
    <CalendarDropdownMultiSelect
      label="Status"
      options={statusFilterOptions}
      selectedItems={values}
      onChange={handleStatusChange}
      onToggleAll={handleToggleAllStatuses}
      renderLabel={(label, value: StatusFilterEnum) => (
        <LabelRow>
          <StatusBullet elementStatus={filterStatusToElementStatusMap[value]} />
          {label}
        </LabelRow>
      )}
    />
  );
};

export default StatusFilter;

const LabelRow = styled(Box)`
  display: flex;
  align-items: center;
`;

const StatusBullet = styled(Bullet, {
  shouldForwardProp: (prop) => prop !== 'elementStatus'
})<{
  elementStatus: ElementInventoryStatus;
}>`
  margin-right: ${({ theme }) => theme.spacing(1)};
  background-color: ${({ theme, elementStatus }) =>
    theme.palette[getColorByElementStatus(elementStatus)][theme.palette.mode === 'dark' ? 'main' : 'dark']};
`;
